/**
 * Team Scoring Adapter
 *
 * Transforms scoring question data into TeamScoringQuestion component props
 */

import type { ScoringQuestionData } from '../types';
import type { MatchStatsResponse } from '@/api/feed/getFeedMatchStats';
import type { Option } from '@/api/questions/getQuestions';

/**
 * TeamScoringQuestion component props
 */
export interface TeamScoringQuestionProps {
  questionText: string;
  questionSubtext?: string;
  questionNumber?: number;
  options: Array<{
    option_id?: string;
    option_text_en: string;
    option_text_fr?: string;
    option_image_en?: string | null;
    option_image_fr?: string | null;
    is_correct: boolean;
    points?: number;
    selectionCount?: number;
    stats?: Record<string, unknown> | Array<{ statType: string; value: number }>;
    grading_data?: Option['grading_data'];
  }>;
  statTypeCode?: string | null;
  points: number;
  status?: 'LIVE' | 'FINAL' | 'SCHEDULED';
  matchStats?: MatchStatsResponse;
}

/**
 * Transforms scoring data into TeamScoringQuestion props
 *
 * @param data - Scoring question data
 * @returns TeamScoringQuestion component props
 */
export function teamScoringAdapter(data: ScoringQuestionData): TeamScoringQuestionProps {
  return {
    questionText: data.questionText,
    questionSubtext: data.questionSubtext,
    questionNumber: data.questionNumber,
    options: data.options.map(opt => ({
      option_id: opt.option_id,
      option_text_en: opt.option_text_en,
      option_text_fr: opt.option_text_fr,
      option_image_en: opt.option_image_en,
      option_image_fr: opt.option_image_fr,
      is_correct: opt.is_correct,
      points: opt.points,
      selectionCount: opt.selectionCount,
      stats: opt.stats,
      grading_data: opt.grading_data,
    })),
    statTypeCode: data.statTypeCode ?? null,
    points: data.totalPoints,
    status: data.status,
    matchStats: data.matchStats,
  };
}
